<script setup>
import notification from "@/utils/plugins/NotificationService";
import { computed, onMounted, ref, watch } from 'vue';
import { useUiStore } from "@/app/stores/uiStore";

import moment from 'moment';

import EmployeeService from '@/api/services/employee.service';
import ResourceService from '@/api/services/resource.service';
import CourseSessionService from '@/api/services/courseSession.service';
import { normalizedIncludes } from "@/utils/textSearch";

// eslint-disable-next-line no-undef
const emit = defineEmits(['closeDialog'])


const employees = ref([]);
const sesionStatus = ref([
    {
        value: "published",
        name: "Publicado"
    },
    {
        value: "planned",
        name: "Planeado"
    },
    {
        value: "ongoing",
        name: "En curso"
    },
    {
        value: "finished",
        name: "Finalizado"
    },
    {
        value: "canceled",
        name: "Cancelado"
    },
    {
        value: "disabled",
        name: "Deshabilitado"
    },
])
const status = ref();

const startDate = ref(new Date());
const endDate = ref();
const location = ref();
const capacity = ref()

const uiStore = useUiStore();

const selectedSession = computed(() => {
    if (props && props.session) {
        return props.session;
    } else {
        return null;
    }
})

// eslint-disable-next-line no-undef
const props = defineProps({
    session: Object,
    course: Object,
})

const employeesSelected = ref([])
const employeeSearchQuery = ref('');

const employeesDepartments = ref([]);
const departmentSelected = ref();
const selectedAll = ref(false);

const filteredEmployees = computed(() => {
    // Todos los empleados originales
    const base = employees.value

    // Seleccionados que NO están ya en base
    const extraSelected = employeesSelected.value.filter(sel =>
        !base.some(emp => emp['@id'] === sel['@id'])
    )

    // Devolvemos todos + los extra que faltan
    const mergedEmployees = [...base, ...extraSelected];

    if (!employeeSearchQuery.value) {
        return mergedEmployees;
    }

    return mergedEmployees.filter(employee => normalizedIncludes(employee.fullName, employeeSearchQuery.value));
})

onMounted(() => {
    if (selectedSession.value) {
        if (selectedSession.value.startAt) {
            startDate.value = new Date(selectedSession.value.startAt);
        }
        if (selectedSession.value.endAt) {
            endDate.value = new Date(selectedSession.value.endAt)
        }
        capacity.value = selectedSession.value.capacity;
        location.value = selectedSession.value.location;
        status.value = selectedSession.value.status;
        employeesSelected.value = selectedSession.value.audienceEmployees;
    }

    getEmployees();
    getDepartments();
})

const closeDialog = () => {
    emit('closeDialog')
}

// eslint-disable-next-line no-unused-vars
const getEmployees = () => {
    const par = {
        'order[name]': 'asc',
        'active': 1,
        page: 1,
        itemsPerPage: 999999,
    };
    if (departmentSelected.value) {
        par['job.department.name'] = departmentSelected.value.name
    }
    uiStore.startLoading();
    EmployeeService.getEmployees(par)
        .then((res) => {
            employees.value = res.data['hydra:member'];
            uiStore.stopLoading();
        })
        .catch(() => {
            uiStore.stopLoading();
        })
}

const getDepartments = (searchQuery = '') => {
    //const params = {query: {name: searchQuery}};
    const params = (searchQuery.length > 0 ? { name: searchQuery } : {});

    ResourceService.getDepartments(params).then((res) => {
        employeesDepartments.value = res.data['hydra:member'];
    })
}

const finishSession = () => {
    if (selectedSession.value) {
        updateSession();
    } else {
        createSession();
    }
}

const updateSession = async () => {
    const id = selectedSession.value.id;
    await uiStore.startLoading();
    const params = {
        capacity: capacity.value,
        location: location.value,
        status: status.value,
        published: true,
        course: props.course['@id'],
    }
    if (startDate.value) {
        params.startAt = formatDate(moment(startDate.value, "DD/MM/YYYY HH:mm").toDate());
    }
    if (endDate.value) {
        params.endAt = formatDate(moment(endDate.value, "DD/MM/YYYY HH:mm").toDate());
    }
    if (employeesSelected.value.length >= 1) {
        params.audienceEmployees = employeesSelected.value.map(emp => emp['@id']);
    }
    CourseSessionService.updateCourseSession(id, params)
        .then((res) => {
            console.log(res);
            if (res && res.status && (res.status === 200 || res.status === 201)) {
                notification.addNotification({
                    title: 'Sesion actualizada',
                    message: 'La sesion ha sido actualizada correctamente',
                    type: 'success'
                });
                closeDialog();
            }
        }).catch((error) => {
            notification.addNotification({
                title: 'Error',
                message: 'Error al actualizar la sesión',
                type: 'error'
            });
            console.log("Error:", error);
        }).finally(async () => {
            await uiStore.stopLoading();
        });
}


const createSession = async () => {
    const params = {
        capacity: capacity.value,
        location: location.value,
        status: status.value,
        published: true,
        course: props.course['@id'],
    }
    if (startDate.value) {
        params.startAt = formatDate(moment(startDate.value, "DD/MM/YYYY HH:mm").toDate());
    }
    if (endDate.value) {
        params.endAt = formatDate(moment(endDate.value, "DD/MM/YYYY HH:mm").toDate());
    }
    if (employeesSelected.value.length >= 1) {
        params.audienceEmployees = employeesSelected.value.map(emp => emp['@id']);
    }

    await uiStore.startLoading();
    CourseSessionService.createCourseSession(params)
        .then((res) => {
            if (res && res.status && (res.status === 200 || res.status === 201)) {
                notification.addNotification({
                    title: 'Sesion creada',
                    message: 'La sesion ha sido creada correctamente',
                    type: 'success'
                });
                closeDialog();
            }
        }).catch((error) => {
            notification.addNotification({
                title: 'Error',
                message: 'Error al crear la sesion',
                type: 'error'
            });
            console.log("Error:", error);
        }).finally(async () => {
            await uiStore.stopLoading();
        });
}

const formatDate = (date) => {
    // Obtener el desplazamiento de la zona horaria
    const offset = new Date().getTimezoneOffset();
    const sign = offset > 0 ? '-' : '+';
    const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
    const minutes = String(Math.abs(offset) % 60).padStart(2, '0');

    // Formato de fecha
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    // Formato de hora
    const hoursOfDay = String(date.getHours()).padStart(2, '0');
    const minutesOfDay = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    // Devolver la cadena en formato ISO con el desplazamiento de la zona horaria
    return `${year}-${month}-${day}T${hoursOfDay}:${minutesOfDay}:${seconds}${sign}${hours}:${minutes}`;
}

const toggleSelectAll = (value) => {
    if (value) {
        // Seleccionar todos los empleados filtrados
        employeesSelected.value = filteredEmployees.value.slice()
    } else {
        // Deseleccionar todo
        employeesSelected.value = []
    }
}
const filterEmployeesByName = (query) => {
    employeeSearchQuery.value = query;
}

// Si cambias de depto, resetear el checkbox
watch(departmentSelected, () => {
    selectedAll.value = false
})



// Disabled dates
const today = ref(new Date())
const isBeforeDateOnly = (current, start) => {
    const c = new Date(current.getFullYear(), current.getMonth(), current.getDate()).getTime();
    const s = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
    return c < s;
}

const calculateDisabledDates = (time) => {
    if (startDate.value) {
        const start = new Date(startDate.value);
        const current = new Date(time);
        // Permitir el mismo día (sin importar hora)
        return isBeforeDateOnly(current, start);
    }
    return false;
}

const dueDatePickerOptions = ref({
    firstDayOfWeek: 1,
    disabledDate: calculateDisabledDates
});

const disabledDateFrom = (date) => {
    const current = new Date(date);
    if (!startDate.value) {
        return disabledDateAfterToday(current);
    }
    const start = new Date(startDate.value);
    return isBeforeDateOnly(current, start);
}

const disabledDateAfterToday = (date) => {
    return date.getTime() < today.value.getTime();
}

const manageChangeTo = (val) => {
    const fechaSeleccionada = new Date(val);

    if (fechaSeleccionada > new Date(today.value)) {
        // Si Start existe y es mayor que End → resetear Start
        if (startDate.value && fechaSeleccionada < new Date(startDate.value)) {
            endDate.value = null;
        }
    }
}

const focusDateEnd = () => {
    if (startDate.value) {
        if (!endDate.value) {

            const start = new Date(startDate.value);
            const end = new Date(start);
            const LIMITE_HORA = 15;

            if (start.getHours() < LIMITE_HORA) {
                end.setHours(LIMITE_HORA, 0, 0, 0);
            } else {
                end.setHours(start.getHours() + 2, start.getMinutes(), 0, 0);
            }

            endDate.value = end;
        }
    }
}

</script>

<template>
    <div>
        <div class="header">
            <img style="width: 30px; margin-bottom: 14px;" src="@/assets/icons/graduating-hat-blue.svg">
            <span class="header-title pl-4">
                Sesión
            </span>
        </div>
        <div class="row f-flex">
            <div class="col-6 el-col-xs-24">
                <div class="form-item date-picker mt-4 w-100 label-common pl-5">
                    <label for="ffrom" class="w-100">Fecha de inicio</label>
                    <el-date-picker id="ffrom" v-model="startDate" type="datetime" class="w-100"
                        placeholder="Fecha de inicio de la sesión" format="DD/MM/YYYY HH:mm" @change="focusDateEnd" />
                </div>
            </div>
            <div class="col-md-6 el-col-xs-24">
                <div class="form-item date-picker mt-4 w-100 label-common pl-5">
                    <label class="w-100" for="date2">Fecha y hora Fin:</label>
                    <el-date-picker v-model="endDate" type="datetime" placeholder="Desde" id="date2"
                        format="DD/MM/YYYY HH:mm" clearable class="mb-1 w-100" :picker-options="dueDatePickerOptions"
                        :disabled-date="disabledDateFrom" @change="manageChangeTo" @focus="focusDateEnd" />
                </div>
            </div>
            <div class="col-6 el-col-xs-24">
                <div class="form-item date-picker mt-4 w-100 label-common pl-5">
                    <label for="capacity" class="w-100">Capacidad</label>
                    <el-input-number id="capacity" v-model="capacity" style="width: 100%;" :min="0" :max="1000000"
                        placeholder="Indique cantidad de personas que se pueden inscribir" :controls="false" />
                </div>
            </div>
            <div class="col-6 el-col-xs-24">
                <div class="form-item date-picker mt-4 w-100 label-common pl-5">
                    <label for="location" class="w-100">Localización</label>
                    <el-input id="location" type="text" v-model="location" placeholder="Indique donde se imparte"
                        :rows="5" />
                </div>
            </div>
            <div class="col-6 el-col-xs-24 mt-3">
                <label for="status" class="w-100">Estado</label>
                <el-select id="status" v-model="status" filterable placeholder="Seleccione el estado de la sesión"
                    :value-key="'@id'">
                    <el-option v-for="stat in sesionStatus" :key="stat.id" :label="stat.name" :value="stat.value">
                    </el-option>
                </el-select>
            </div>
            <div class="col-md-12 el-col-xs-24 mt-3 d-flex row">
                <div class="el-col-xs-24 col-md-6">
                    <label for="departments" class="w-100">Departamento:</label>
                    <el-select id="departments" v-model="departmentSelected" filterable placeholder="TODOS"
                        @change="getEmployees" :value-key="'@id'" clearable>
                        <el-option v-for="dept in employeesDepartments" :key="dept.id" :label="dept.name" :value="dept">
                        </el-option>
                    </el-select>
                </div>
                <div class="el-col-xs-24 col-md-6 d-flex mt-4 ">
                    <el-checkbox v-model="selectedAll" if="selectedALl" size="large" label="Seleccionar todos"
                        @change="toggleSelectAll" />
                </div>
            </div>
            <div class="col-12 mt-3">
                <label for="selEmployee" class="w-100">Audiencia</label>
                <el-select id="selEmployee" v-model="employeesSelected" filterable
                    placeholder="Seleccione los empleados para la sesión" multiple collapse-tags collapse-tags-tooltip
                    :value-key="'@id'" class="w-100 selEmployee" :filter-method="filterEmployeesByName">
                    <el-option v-for="employee in filteredEmployees" :key="employee['@id']" :label="employee.fullName"
                        :value="employee">
                    </el-option>
                </el-select>
            </div>
            <div class="col-12 mt-2" v-if="employeesSelected">
                <label for="tableEmployees" class="w-100">Lista de empleados asociados</label>
                <div class="row">
                    <template v-for="emp in employeesSelected" :key="emp['@id']">
                        <div class="col-6 el-col-xs-24">
                            {{ emp.fullName }}
                        </div>
                    </template>
                </div>
                <div v-if="employeesSelected && employeesSelected.length < 1">
                    No hay empleados asociados
                </div>
            </div>
        </div>
        <!--  Botones guardar y cancelar -->
        <div class="row footer">
            <div class="col-md-12 mt-4" style="margin-left: -10px;">
                <div class="d-flex justify-content-end gap-3">
                    <div class="col-md-3">
                        <button class="btn btn-block btn_yellow edit-button" @click="closeDialog" style="">Cancelar
                        </button>
                    </div>
                    <div class="col-md-3">
                        <button type="submit" class="btn btn-block btn_red edit-button" @click="finishSession">
                            {{ selectedSession != null ? 'ACTUALIZAR' : 'CREAR' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.header-title {
    font-size: 30px;
    margin-left: 5px;
    font-weight: 800;
    letter-spacing: -0.05em;
    color: #474ce6;
}

.custom-textarea:deep(.el-textarea__inner) {
    font-size: 14px !important;
    border-radius: 20px;
}

@media (max-width: 500px) {
    .selEmployee {
        :deep(.el-tag) {
            max-width: 99px !important;
        }
    }
}
</style>
